import type { ProjectConfig } from '../project'

export const tscheppaConfig: ProjectConfig = {
  id: 'tscheppa',
  name: 'Tscheppaschlucht',

  api: {
    artrackUrl: 'https://api-artrack.arkturian.com',
    storageUrl: 'https://api-storage.arkturian.com',
    knowledgeUrl: 'https://knowledge-api.arkturian.com',
    // Öffentlicher Leseschlüssel (Konto tscheppa-public@api, Issue #2385): darf GET sowie
    // POST /snap und /context-at, sonst nichts. Nur solche Schlüssel gehören ins Bundle.
    apiKey: 'qrBF699Eaz7qv59ICRSlP8IcmSTZEAhH',
    trackId: 30,
  },

  map: {
    type: 'tiles',
    tiles: {
      manifest: '/tiles/manifest.json',
      calibration: '/data/calibration-points.json',
    },
    center: { lat: 46.489, lng: 14.274 },
    defaultZoom: 4,
    backgroundColor: '#2d4a2d',
  },

  pins: {
    defaultStyle: 'dot',
    categoryIcons: {
      poi: '📍', navigation: '🧭', amenity: '🏪', transport: '🚌',
      nature: '🌲', media: '📸', system: '⚙️',
    },
  },

  theme: {
    primary: '#C4A96A',
    background: '#1A1F16',
    text: '#F7F5F0',
    fontHeading: "'Space Grotesk', system-ui, sans-serif",
    fontBody: "'Inter', system-ui, sans-serif",
    logo: '/logo.svg',
  },

  features: {
    audioGuide: true,
    stories: true,
    kioskMode: true,
    species: true,
    navigation: true,
    routeIntelligence: true,
    threeD: true,
    /* An, solange die Web-App die Erzaehlung selbst spielt. Im iOS-Webview
       uebernimmt sie der Wirt zur Laufzeit ueber `audio:narration:native`. */
    storyNarration: true,
  },

  bounds: {
    minLat: 46.475,
    maxLat: 46.500,
    minLng: 14.250,
    maxLng: 14.295,
  },

  defaultRouteId: 56,
  routeIds: [56, 60, 63, 65],

  // Entdecken-Highlights: Tschaukofall zuerst, dann die weiteren Video-Sights
  // (Wasserüberlauf, Hohe Brücke, Elfenquelle) — Alex 2026-07-18, 114564.
  discover: {
    highlightOrder: [26378, 24719, 26430, 24724],
  },

  // Route curation — moved verbatim from NavigatePage hardcodes (tenant
  // template, Post 4242). Picker order Alex 2026-07-24: 1 Tschaukofall ·
  // 2 Kunstweg (zweitrelevant) · 3 Bodental · 4 Märchenwiese (Ausnahmeweg).
  // Ziel names: Deutscher Peter for 56/60; Bodental (63) ends at Gasthof
  // Sereinig (ArTrack wp 24705), Bodental 2 (65) at Gasthof Kirchenwirt
  // (wp 24703).
  routesCuration: {
    order: [56, 60, 65, 63],
    // The visitor experience starts at the KASSA am goldenen Brünnl (~1.26 km
    // along the trail, wp 24710 / Goldenes Bründl wp 26381) — NOT at the
    // Empfangshaus where the recorded trace begins (Alex 2026-07-16). The
    // coords are the onto-route SNAPPED Kassa position (Tschepp, ArTrack-
    // verified) so the teardrop sits exactly on the line; geometry keeps the
    // genuinely walked Parkplatz→Kassa stretch.
    // Alex 114501: Pill nur „Kassa" (kein Subtitle) + €-Glyph im Kopf.
    startTitle: 'Kassa',
    startGlyph: 'euro',
    startMarker: { lat: 46.488919878724936, lng: 14.280217354835095 },
    // Der Tropfen ersetzt den Ballon von wp 24710 „Kassa" — ein Tipp darauf
    // muss deshalb dessen Detail oeffnen, nicht bloss die Kamera bewegen.
    startMarkerWaypointId: 24710,
    // Ziel-Pill ohne Subtitle (Alex 2026-07-17) — nur „Ziel".
    highlight: { 56: { lat: 46.48777826, lng: 14.26374383, title: 'Tschaukofall' } },
    // Besucherzentrum (wp 24752, route km 0): physical start AND end for
    // visitors (the bus returns them to the parking lot) — full teardrop in
    // brand gold instead of the former info balloon (Alex 114500). The
    // balloon itself is soft-disabled via ArTrack settings.display.enabled
    // (render-only gate; story scene 1 / guide / proximity data unaffected).
    // "i"-Glyph im Teardrop — die Info-Semantik des alten Badges in der
    // Start/Ziel-Form. scale 0.6 = Tschaukofall-Highlight-Größe (Alex 114503).
    // Label „Empfangshaus" (Alex 30.07., Shot 116525): der orange Pin soll
    // eine Text-Pille tragen wie Kassa/Tschaukofall/Ziel — hebt das frühere
    // „kein Label"-Feintuning vom 17.07. bewusst auf.
    extraMarkers: [{ lat: 46.497945467799866, lng: 14.284254946900376, title: 'Empfangshaus', glyph: 'i', color: '#F59E0B', scale: 0.6, waypointId: 24752 }],
  },

  // 3D presenter embed (Bridge Contract v0.2, Post 4263). georef = FINAL
  // calibration by 3DPresenter 2026-07-18 (RANSAC+Umeyama over 10 verified
  // WPT↔manual-waypoint matches, median residual 25.6 m; Teufelsbrücke 3.4 m).
  // Values live in the contract product section — change them THERE first.
  presenter3d: {
    frameUrl: 'https://presenter.arkturian.com/?app=map&embed=1',
    georef: {
      originLat: 46.48581728,
      originLng: 14.26634508,
      metersPerUnit: 1.15656,
      rotationDeg: -102.9057,
      originModel: { x: -681.66, y: 0, z: 229.15 },
      zNorthPositive: true,
    },
  },

  /* Die generische Terrain-Welt (Post 4704) — EIN Deployment traegt beide
   * Apps. Doppel-Gate (map+terrain) auf Stand 5408750 bestanden und von
   * GeoMap + 3DPresenter freigegeben am 2026-08-15 (Post 4704, #1123). */
  terrain3d: {
    frameUrl: 'https://presenter.arkturian.com/?app=terrain&embed=1',
  },

  // v0.5 Track-LOD (#1159): das Register für setTracks. Schwellen wie im
  // Kärnten-Register (GeoMap): Detail < 4 km, Silhouette < 15 km.
  tracks3d: [
    { trackId: 30, name: 'Tscheppaschlucht', lat: 46.489, lng: 14.274, detailMeter: 4000, silhouetteMeter: 15000 },
  ],

  // Map-viewer extras — moved verbatim from the former hardcoded
  // tileMapConfig.ts (tenant-template merge, Post 4242: one tenant = ONE
  // config file). buildTileMapConfig() combines this with the fields above.
  tileMap: {
    map: {
      type: 'tiles',
      manifestPath: '/tiles/manifest.json',
      tilesBasePath: '/tiles',
      calibrationPath: '/data/calibration-points.json',
      fallbackCalibrationPoints: [
        { pixel: { x: 11868, y: 8113 }, latLng: { lat: 46.488019391437824, lng: 14.265172353352058 } },
        { pixel: { x: 10376, y: 10333 }, latLng: { lat: 46.478113892607844, lng: 14.255945533891648 } },
        { pixel: { x: 14979, y: 5709 }, latLng: { lat: 46.49793484361013, lng: 14.28423817024073 } },
      ],
      imageSize: { width: 53848, height: 43290 },
      // Pannable content rect — measured 2026-06-01 via F9 debug overlay.
      // The full image (0..53848, 0..43290) has empty void around the actual
      // map. Constraining pan to this rect keeps the rubber-band useful and
      // prevents users from drifting into the blank periphery.
      contentBounds: {
        minX: 8794,
        minY: 7000,
        maxX: 44000,
        maxY: 31986,
      },
      // Alex' field-measured USER zoom-out floor, restored (Issue #315 fix,
      // tile-map 2.1.195 + canvas-engine 1.1.31). 0.08 sits ABOVE the
      // programmatic initial/route-framing zooms (0.0335 portrait / 0.0576
      // desktop) — under tile-map ≤2.1.193 that clamped the initial view and
      // broke the centered overview (Alex regression 2026-07-21). Since 2.1.195
      // a configured floor sets `clampProgrammaticScale=false`: user wheel/pinch
      // still clamp to this floor, but the programmatic initial view + balloon-
      // focus zooms bypass it — so Alex gets his void-guard AND the centered
      // start, no forced trade-off anymore.
      minScale: 0.08,
      // NO maxScale: a 0.67 zoom-in cap also clamped the programmatic
      // balloon-focus zooms (species 2.0046, POI 0.9781, mobile tap 3.5) —
      // clicking a flora/fauna balloon landed too far away. Alex reverted the
      // cap (2026-07-20): zoom-in behaves as before (engine default), only the
      // zoom-OUT floor above stays.
      // Pannbarer Slack achsgetrennt in WELT-Units (#494, tile-map ≥2.1.212).
      // Zweck: Route-Framing schiebt die Karte off-centre in die freie Bahn
      // zwischen den UI-Insets (Bottom-Sheet unten, Panels seitlich), damit
      // weder Start (oben) noch Ziel (unten/hinterm Sheet) verdeckt ist. Der
      // Shift ist eine feste SCHIRM-Distanz → der Welt-Slack dafür ist
      //   |inset_move_px| / framedScale  — unabhängig von der Kartengröße,
      // deshalb Welt-Units statt einer Fraction (0.6/0.1 waren blind getunt).
      //
      // Herleitung (Insets aus NavigatePage, framedScaleMin 0.0335 portrait /
      // 0.0576 desktop — die längste Route rahmt am weitesten raus):
      //   Y: moveY = (topInset 130 − bottomInset 289)/2 ≈ 80px / 0.0335 ≈ 2373
      //   X: moveX = (leftInset 420 − rightInset 160)/2  = 130px / 0.0576 ≈ 2257
      //   → { x: 2900, y: 5300 }
      //
      // bottomInset 289 = 224 + footerHeight. footerHeight wird zur LAUFZEIT per
      // ResizeObserver an `.footer-nav` gemessen (NavigatePage:824); 65 ist nur
      // der Startwert und waechst bei Schriftskalierung oder Safe-Area. Deshalb
      // traegt padY bewusst Reserve: 5300 ist ~2,2x des realen Bedarfs (2373)
      // und deckt bis bottomInset ~485 ab.
      //
      // ACHTUNG beim Nachrechnen: Bis 2026-07-30 stand hier `bottomInset ~400
      // -> 4030`. Das war zu hoch gegriffen (der Footer misst ~65, nicht ~176).
      // Wer mit 4030 nachrechnet, haelt 5300 fuer knapp bemessen — es ist das
      // Gegenteil. Die Zahlen oben sind die aus dem Code gelesenen.
      //
      // Obergrenze (void-frei): contentBounds ist ein CROP aus dem Kachelbild
      //   53848×43290. Rand bis zur Bildkante: links 8794 · rechts 9848 · oben
      //   7000 · unten 11304 → padX ≤ 8794, padY ≤ 7000. Darüber zieht man über
      //   die Bildkante in den Hintergrund (#2d4a2d). Fenster, in dem BEIDE
      //   Bedingungen gelten: 4030 ≤ padY ≤ 7000 · 2257 ≤ padX ≤ 8794 —
      //   {2900, 5300} liegt in beiden, kein Void-Trade.
      //   (Historie: 0.6 → padY 14992 → 7992 Void → 639px/844 ≈ 2/3 grün, 116356.
      //    0.1 → padY 2499 void-frei, aber < 4030 → Ziel-Pin hinterm Sheet.)
      // Die Lib warnt (console), falls dieser Pad je den Bildrand ODER den
      // realen Framing-Bedarf einer Route überschreitet — wächst das Sheet,
      // meldet sie sich, statt still den Ziel-Pin zu verdecken.
      boundsPad: { x: 2900, y: 5300 },
      // Ballon-Verschieben per Touch: auf Mobile AUS (#518, tile-map ≥2.1.214).
      // Grund (Tschepp am Code verifiziert): ein Ballon-Drag hinterlässt am
      // Ballon-Verschieben: Alex hat es am 30.07. ausdruecklich GEWOLLT
      // („ballon verschieben will ich haben warum nicht?"). Deshalb steht hier
      // KEINE Schwelle — Legacy-Verhalten, an jedem Zoom verschiebbar.
      //
      // Vorher stand hier `balloonDragMinScale: Infinity` (ganz aus), auf meine
      // Empfehlung hin und BEVOR Alex geantwortet hatte. Das haette seine
      // Entscheidung invertiert ausgeliefert, deshalb entfernt.
      //
      // Der Wert kommt zurueck, sobald zwei Dinge geklaert sind (CHAP2 fragt):
      //   1. Soll der Ballon am Handy LIEGEN BLEIBEN? Aktuell setzt onTouchEnd
      //      `isFixed = false`, er schwimmt sofort zurueck — das Ziehen hat
      //      dort also kein Ergebnis. Am Desktop bleibt er liegen.
      //   2. Erst danach ist die Schwelle sinnvoll zu waehlen: Sie soll das
      //      versehentliche Greifen beim rausgezoomten Wischen verhindern
      //      (#518), ohne das gewollte Verschieben zu nehmen.
      // Bis dahin gilt Alex' Wort, nicht meine Empfehlung.
    },

    // Label force-graph physics — repulsion slider-tuned by Alex 2026-07-03
    // (default was 53000; high value spreads labels apart aggressively).
    physics: {
      // Lowered 25000 → 5000 (Alex, Issue #356, 2026-07-23): with many more
      // pins on the map the aggressive repulsion made the label layout shaky;
      // a gentler charge keeps labels closer to their pin and settles calmer.
      repulsionStrength: 5000,
      // Rigid rod edges: exact, uniform edge length for every balloon —
      // repulsion rotates labels around their pin instead of stretching
      // the connector (Alex 2026-07-03: no more varying edge lengths).
      edgeType: 'rigid',
      // Object-based engine: rod lengths are construction parameters —
      // labels orbit their pin, stretching is mathematically impossible.
      engine: 'orbital',
      // Connector length = balloon diameter × 1.7 (Alex' rule, 2026-07-03):
      // each balloon's connector scales with its own rendered size — small
      // species pins short, the Deutscher-Peter hero balloon long, no
      // per-category tuning needed. Verified against the approved look:
      // flora/fauna d≈90 → 153px (matches the old 0.85 factor), hero
      // d≈240 → 408px (matches "double length").
      edgeLengthFactor: 1.7,
      // Transport badges (P, bus) still collapse to a stub right next to
      // their pin — that's a semantic exception (the badge nearly IS the
      // place), not a size effect, so it stays a category multiplier.
      edgeScaleByCategory: {
        transport: 0.12,
        // Field-tuned by Alex (mobile physics panel, 2026-07-03): flora and
        // Sehenswürdigkeiten sit closer to their anchor. Multiplies on top of
        // the edgeLengthFactor-derived base length.
        flora: 0.6,
        poi: 0.6,
        navigation: 0.4,
        // Issue #397 (Alex, Shot 115768): die Jausenstation hing so weit vom
        // Anker weg, dass nicht mehr ablesbar war, wo sie eigentlich liegt —
        // „40% kleiner der Abstand". Gilt für die ganze Kategorie, weil Alex
        // den Ballon-TYP meinte, nicht den einen Wegpunkt.
        // Unberührt bleiben die zwei amenity-POIs mit eigenem edge_scale
        // (Deutscher Peter = 1, Hubschrauberlandeplatz = 0.12): der Per-POI-
        // Wert schlägt die Kategorie, eine bewusst gesetzte Länge soll ein
        // Kategorie-Default nicht überschreiben.
        amenity: 0.6,
      },
    },

    view: {
      center: { lat: 46.489, lng: 14.274 },
      defaultZoom: 0.1,
      backgroundColor: '#2d4a2d',
    },

    svgOverlays: {
      globalScale: 3.9189325748305284,
      // Match Figma-design intent: SVG strokes carry `mix-blend-mode:screen`
      // inline (designer authored on a white Figma board), but canvas
      // drawImage ignores CSS mix-blend — applied via globalCompositeOperation
      // here. 'screen' brightens the saturated red border and cyan rivers
      // against the dark-green satellite tiles, matching the Figma reference.
      blendMode: 'screen',
      overlays: [
        // Rivers load with hidden:true (Alex 2026-07-04: launch view = ONE
        // clean selected route) and are shown on demand via the legend's
        // Gewaesser toggle (Alex 2026-08-16). The red gorge border stays off.
        { id: 'fluss_west', hidden: true, url: '/svgs/fluss_west.svg', name: 'Fluss West', x: 14856, y: 15827 },
        { id: 'fluss_hauptbach', hidden: true, url: '/svgs/fluss_hauptbach.svg', name: 'Fluss Hauptbach', x: 24971, y: 15333 },
        { id: 'fluss_nebenbach', hidden: true, url: '/svgs/fluss_nebenbach.svg', name: 'Fluss Nebenbach', x: 26258, y: 18945 },
        { id: 'strasse', url: '/svgs/strasse.svg', name: 'Strasse', x: 25191, y: 10185 },
        { id: 'fluss_sued', hidden: true, url: '/svgs/fluss_sued.svg', name: 'Fluss Süd', x: 17796, y: 19114 },
        // { id: 'umrandung', url: '/svgs/umrandung.svg', name: 'Umrandung', x: 24482, y: 13927 },
      ],
    },

    routeStyle: {
      color: '#00FF88',
      darkenFactor: 0.5,
      // Route-picker mode: only the selected route is drawn; the sheet
      // carousel swaps which one (Alex 2026-07-04).
      showOnlySelected: true,
      // 29.07., zwei Stunden Geschichte in drei Zeilen: Die Linie war hier
      // kurz auf 3/6-px-Anker + Saum-Zuschlag verbreitert (tile-map 2.1.209,
      // Anlass „mega schlecht"-Feedback vom Vormittag). Alex hat das Ergebnis
      // auf der Karte gesehen und zurueckgewiesen — Post 4382: „bitte
      // rueckgaengig machen, das schaut jetzt nicht mehr gut aus". Die
      // Faehigkeit bleibt in der Lib (RouteStyleConfig.lineWidth/casing),
      // hier bewusst KEINE Werte mehr: Vorgaben der Lib = der alte Look.
    },

    kiosk: {
      enabled: true,
      startPointName: 'Empfangshaus',
      startPosition: { lat: 46.497938, lng: 14.284227 },
    },

    theme: {
      primary: '#C4A96A',
      routeColor: '#00FF88',
      placeholderImageId: 102998,
    },
  },
}
